import { useState, createContext, useEffect, useCallback } from 'react';
import { LayoutState, ChildContainerProps, LayoutConfig, LayoutContextProps } from 'src/types/types';

const THEME_STORAGE_KEY = 'iam-econtract-dark-mode';

const loadThemeCSS = (themeName: string) => {
  const linkId = 'theme-css-link';
  let linkEl = document.getElementById(linkId) as HTMLLinkElement | null;
  const href = `/themes/${themeName}/theme.css`;

  if (!linkEl) {
    linkEl = document.createElement('link');
    linkEl.id = linkId;
    linkEl.rel = 'stylesheet';
    linkEl.type = 'text/css';
    document.head.appendChild(linkEl);
  }
  linkEl.href = href;
};

const getSavedDarkMode = (): boolean => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const LayoutContext = createContext({} as LayoutContextProps);

export const LayoutProvider = ({ children }: ChildContainerProps) => {

  const savedDark = getSavedDarkMode();

  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig>({
    ripple: false,
    inputStyle: 'outlined',
    menuMode: 'static',
    colorScheme: savedDark ? 'dark' : 'light',
    theme: savedDark ? 'lara-dark-blue' : 'lara-light-blue',
    scale: 14
  });

  const [layoutState, setLayoutState] = useState<LayoutState>({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false
  });

  // Load theme CSS on mount and when theme changes
  useEffect(() => {
    loadThemeCSS(layoutConfig.theme);
    // Apply dark class to body for custom CSS overrides
    if (layoutConfig.colorScheme === 'dark') {
      document.documentElement.classList.add('app-dark');
    } else {
      document.documentElement.classList.remove('app-dark');
    }
  }, [layoutConfig.theme, layoutConfig.colorScheme]);

  const toggleDarkMode = useCallback(() => {
    setLayoutConfig(prev => {
      const isDark = prev.colorScheme === 'light';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, String(isDark));
      } catch { /* ignore */ }
      return {
        ...prev,
        colorScheme: isDark ? 'dark' : 'light',
        theme: isDark ? 'lara-dark-blue' : 'lara-light-blue',
      };
    });
  }, []);

  const onMenuToggle = () => {
    if (isOverlay()) {
      setLayoutState((prevLayoutState) => ({ ...prevLayoutState, overlayMenuActive: !prevLayoutState.overlayMenuActive }));
    }

    if (isDesktop()) {
      setLayoutState((prevLayoutState) => ({ ...prevLayoutState, staticMenuDesktopInactive: !prevLayoutState.staticMenuDesktopInactive }));
    } else {
      setLayoutState((prevLayoutState) => ({ ...prevLayoutState, staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive }));
    }
  };

  const showProfileSidebar = () => {
    setLayoutState((prevLayoutState) => ({ ...prevLayoutState, profileSidebarVisible: !prevLayoutState.profileSidebarVisible }));
  };

  const isOverlay = () => {
    return layoutConfig.menuMode === 'overlay';
  };

  const isDesktop = () => {
    return window.innerWidth > 991;
  };

  const value: LayoutContextProps = {
    layoutConfig,
    setLayoutConfig,
    layoutState,
    setLayoutState,
    onMenuToggle,
    showProfileSidebar,
    toggleDarkMode
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};
